import { FunctionComponent, useEffect, useState, useCallback } from 'react'
import useStyles from './styles'
import { capitalize } from '@/utils/fnUtils'
import { getTheme, Space, Text } from '@conexasaude/conexa-hero'
import { DateTime } from 'luxon'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { RenderIf } from '../RenderIf'
import { TimezoneProps } from './types'
import { SEND_TO } from '@/modules/Signup/constants'
import { AnalyticEvent } from '@/modules/Signup/types/en-us'
import ClariHelper from '../Clari'

const CustomTimezone: FunctionComponent<TimezoneProps> = ({
  showSupportButton = true,
  supportTextButton,
  showfooterCopyrightText = true,
  tagEventName,
  tagInteraction,
  cnxUserId,
  idClinic,
  additionalFooterSpacing = false,
  showRegisterPsychologistButton = false,
  showTimeZone = true,
}) => {
  const styles = useStyles({ additionalFooterSpacing })
  const [parsedDate, setParsedDate] = useState<string>('')
  const { logAnalyticsEvent } = useFirebaseContext()
  const theme = getTheme()

  const handleSupportOpening = () => {
    if (tagEventName && tagInteraction)
      settingUpAnalyticsEvent({
        eventName: tagEventName,
        interaction: tagInteraction,
        ...(cnxUserId && { cnx_user_id: cnxUserId }),
        ...(cnxUserId && { user_id: cnxUserId }),
        ...(idClinic && { idClinic: idClinic }),
      })
  }

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      logAnalyticsEvent(eventDefinitions?.eventName, {
        interaction: `${eventDefinitions?.interaction}`,
        ...(eventDefinitions?.cnx_user_id && {
          cnx_user_id: eventDefinitions?.cnx_user_id,
        }),
        ...(eventDefinitions?.user_id && {
          user_id: eventDefinitions?.user_id,
        }),
        ...(eventDefinitions?.idClinic && {
          idClinic: eventDefinitions?.idClinic,
        }),
        options: {
          sendTo: SEND_TO,
        },
      })
    },
    [logAnalyticsEvent],
  )

  const parseDate = useCallback((date: DateTime) => {
    const parsedDate = date?.toFormat?.('dd/LL/yyyy, EEEE - HH:mm:ss')
    const timeZoneText = 'Brasil (UTC-3) - Horário de Brasília'
    return `${capitalize(parsedDate)} - ${timeZoneText}`
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      const dateTimeNow = DateTime.now().setZone('America/Sao_Paulo')

      const parseDateTimeNow = parseDate(dateTimeNow)
      setParsedDate(parseDateTimeNow)
    }, 1000)

    return () => clearInterval(interval)
  }, [parseDate])

  return (
    <div css={styles.contentFooter}>
      <RenderIf condition={showSupportButton}>
        <ClariHelper
          supportTextButton={supportTextButton}
          showShotcut={false}
          callback={handleSupportOpening}
        />
      </RenderIf>

      <RenderIf condition={showRegisterPsychologistButton}>
        <a
          href={import.meta.env.VITE_APP_REGISTER_PSYCHOLOGIST_URL}
          target="_blank"
          rel="noreferrer"
        >
          <Text
            variant="linkUnderline"
            renderAs="span"
            color={theme.colors.brand.primary[300]}
          >
            Cadastro para Psicólogo(a)
          </Text>
        </a>

        <Space mb="xs" />
      </RenderIf>

      <RenderIf condition={showTimeZone}>
        <Text variant="microCopy" textAlign="center">
          {parsedDate}
        </Text>
      </RenderIf>
      <RenderIf condition={showfooterCopyrightText}>
        <Text
          css={styles.allRightsReserved}
          renderAs="span"
          variant="microCopy"
          textAlign="center"
        >
          © {new Date()?.getFullYear()} Conexa Saúde · Todos os direitos
          reservados.
        </Text>
      </RenderIf>
    </div>
  )
}

export default CustomTimezone
